/** Pure helpers for the work-model screens (ordering, progress, policy, latency). */
import type {
  AutonomyLevel,
  BuildCard,
  Landing,
  LoopProfile,
  ProjectConfigLite,
  TaskDetail,
  WorkTaskStatus,
} from './types.js'

const OPEN: readonly WorkTaskStatus[] = ['ready', 'running', 'blocked', 'review', 'landing']

/**
 * Tasks in execution order: dependencies first (Kahn), then running/blocked before ready,
 * then priority, then age. Cycles and unknown deps never drop a task.
 */
export function orderTasks(tasks: readonly TaskDetail[]): TaskDetail[] {
  const byId = new Map(tasks.map((t) => [t.id, t]))
  const rank = (t: TaskDetail) =>
    t.status === 'running' ? 0 : t.status === 'blocked' ? 1 : t.status === 'review' ? 2 : 3
  const cmp = (a: TaskDetail, b: TaskDetail) =>
    rank(a) - rank(b) || b.priority - a.priority || a.createdAt - b.createdAt
  const indeg = new Map<string, number>()
  const out = new Map<string, string[]>()
  for (const t of tasks) {
    const deps = t.deps.filter((d) => byId.has(d) && d !== t.id)
    indeg.set(t.id, deps.length)
    for (const d of deps) out.set(d, [...(out.get(d) ?? []), t.id])
  }
  const ready = tasks.filter((t) => (indeg.get(t.id) ?? 0) === 0).sort(cmp)
  const result: TaskDetail[] = []
  const seen = new Set<string>()
  while (ready.length) {
    const t = ready.shift() as TaskDetail
    if (seen.has(t.id)) continue
    seen.add(t.id)
    result.push(t)
    for (const n of out.get(t.id) ?? []) {
      const left = (indeg.get(n) ?? 1) - 1
      indeg.set(n, left)
      if (left === 0) {
        ready.push(byId.get(n) as TaskDetail)
        ready.sort(cmp)
      }
    }
  }
  // Cycles: append the rest in a stable order.
  for (const t of [...tasks].sort(cmp)) if (!seen.has(t.id)) result.push(t)
  return result
}

/** Deps of `t` that are not done yet (what blocks it from being dispatched). */
export function openDeps(t: TaskDetail, all: readonly TaskDetail[]): TaskDetail[] {
  const byId = new Map(all.map((x) => [x.id, x]))
  return t.deps
    .map((d) => byId.get(d))
    .filter((d): d is TaskDetail => !!d && d.status !== 'done' && d.status !== 'cancelled')
}

export function progressOf(tasks: readonly { status: string }[]) {
  const live = tasks.filter((t) => t.status !== 'cancelled' && t.status !== 'proposed')
  const done = live.filter((t) => t.status === 'done').length
  return { done, total: live.length, pct: live.length ? Math.round((100 * done) / live.length) : 0 }
}

export function isOpen(status: string) {
  return (OPEN as readonly string[]).includes(status)
}

// ---- autonomy (mirror of @viko/project-config effectivePolicy) ------------------------------------

export const AUTONOMY_ACTIONS = [
  ['add-task', 'Add tasks it discovers'],
  ['pick-machine', 'Pick the machine'],
  ['start-run', 'Start runs for ready tasks'],
  ['session-rotation', 'Rotate long sessions'],
  ['prune-landed-worktree', 'Prune landed worktrees'],
  ['new-workstream', 'Open new workstreams'],
  ['land-main', 'Land to main'],
  ['ship-internal', 'Ship internal builds'],
  ['switch-agent', 'Switch Claude ↔ Codex'],
  ['accept-agent-tasks', 'Accept agent-proposed tasks'],
  ['promote-rules', 'Promote learned rules'],
  ['ship-production', 'Ship to production'],
  ['delete-unmerged', 'Delete unmerged work'],
  ['use-secret', 'Use a secret'],
] as const

export type AutonomyAction = (typeof AUTONOMY_ACTIONS)[number][0]

export const HARD_ASK: readonly AutonomyAction[] = [
  'ship-production',
  'delete-unmerged',
  'use-secret',
]

export function effectivePolicy(
  cfg: { loopProfile?: LoopProfile; autonomy?: ProjectConfigLite['autonomy'] },
  action: AutonomyAction,
): 'auto' | 'ask' {
  if (HARD_ASK.includes(action)) return 'ask'
  const override = cfg.autonomy?.overrides?.[action]
  if (override) return override
  const loop = cfg.loopProfile ?? 'fast'
  const profileAuto: Record<LoopProfile, AutonomyAction[]> = {
    fast: ['land-main', 'ship-internal'],
    balanced: ['ship-internal'],
    careful: [],
  }
  if (profileAuto[loop].includes(action)) return 'auto'
  const level: AutonomyLevel = cfg.autonomy?.level ?? 'balanced'
  if (level === 'careful') return 'ask'
  if (
    (
      [
        'add-task',
        'pick-machine',
        'start-run',
        'session-rotation',
        'prune-landed-worktree',
      ] as const
    )
      .map(String)
      .includes(action)
  )
    return 'auto'
  if (level === 'high' && action !== 'accept-agent-tasks' && action !== 'promote-rules')
    return 'auto'
  return 'ask'
}

export const LOOP_HELP: Record<LoopProfile, string> = {
  fast: 'Land to main and ship internal builds without asking.',
  balanced: 'Ship internal builds automatically; ask before landing.',
  careful: 'Ask before landing and before every build.',
}

// ---- releases ------------------------------------------------------------------------------------

export function median(xs: number[]): number | null {
  if (!xs.length) return null
  const s = [...xs].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? (s[m] as number) : ((s[m - 1] as number) + (s[m] as number)) / 2
}

/**
 * Feedback → build latency: for each task a build shipped, the time from the task's creation
 * (feedback on a build becomes a task or a message in the live run) to the build becoming
 * available. Median over the window.
 */
export function feedbackToBuildMs(
  builds: readonly BuildCard[],
  tasks: readonly Pick<TaskDetail, 'id' | 'createdAt'>[],
): number | null {
  const created = new Map(tasks.map((t) => [t.id, t.createdAt]))
  const out: number[] = []
  for (const b of builds) {
    if (b.status !== 'available' || !b.updatedAt) continue
    for (const id of b.taskIds) {
      const c = created.get(id)
      if (c !== undefined && b.updatedAt > c) out.push(b.updatedAt - c)
    }
  }
  return median(out)
}

/** Landing → available build, median (how long a landed change waits for a build). */
export function landToBuildMs(
  builds: readonly BuildCard[],
  landings: readonly Landing[],
): number | null {
  const out: number[] = []
  for (const b of builds) {
    if (b.status !== 'available' || !b.updatedAt) continue
    const l = landings.find(
      (x) => x.state === 'landed' && x.taskIds.some((t) => b.taskIds.includes(t)),
    )
    if (l && b.updatedAt > l.updatedAt) out.push(b.updatedAt - l.updatedAt)
  }
  return median(out)
}

export function formatDur(ms: number | null): string {
  if (ms === null) return '—'
  const m = Math.round(ms / 60_000)
  if (m < 60) return `${Math.max(1, m)} min`
  const h = m / 60
  if (h < 48) return `${h.toFixed(h < 10 ? 1 : 0)} h`
  return `${Math.round(h / 24)} d`
}
